import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  useMediaQuery,
  Stack,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import api, { checkActiveWebhookByTopic } from '../../../services/api';
import ServerDataTable from '../../ServerDataTable/ServerDataTable';
import JsonDataDisplay from '../../common/JsonDataDisplay';
import { formatDate } from '../../../utils/formatDate';
import { useConfigurationStatus } from '../../../hooks/useConfigurationStatus';

export default function SyncedCustomersTab({
  tabChange
}) {
  // URL synchronization constants
  const DEFAULT_PAGE_SIZE = 10;

  // State for search and pagination
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewData, setViewData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  // Filter states - separate state for form inputs and applied filters
  const [filterForm, setFilterForm] = useState({
    updated_at_start: '',
    updated_at_end: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    updated_at_start: '',
    updated_at_end: ''
  });

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Function to update URL with filters and pagination
  const updateURLWithFilters = (filters, currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams(location.search);

    // Clear existing filter parameters
    Object.keys(filters).forEach(key => {
      params.delete(key);
    });

    // Add non-empty filter values to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
      params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());
      params.set('search', currentSearchTerm || '');
    }

    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Initialize filters and pagination from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Parse filter parameters
    const filtersFromURL = {
      is_deleted: params.get('is_deleted') || '',
      last_action: params.get('last_action') || '',
      updated_at_start: params.get('updated_at_start') || '',
      updated_at_end: params.get('updated_at_end') || ''
    };

    // Parse pagination parameters
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');
    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(newPageIndex);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }

    // Set applied filters from URL
    setAppliedFilters(filtersFromURL);
    setFilterForm(filtersFromURL);
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      // Only update if the current pageIndex doesn't match the URL
      if (pageIndex !== urlPageIndex) {
        setPageIndex(urlPageIndex);
      }
    }
  }, [location.search, pageIndex]);

  // Sync modal filters with applied filters when dialog opens
  useEffect(() => {
    if (isFilterDialogOpen) {
      setFilterForm(appliedFilters);
    }
  }, [isFilterDialogOpen, appliedFilters]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm?.trim());
      setPageIndex(0); // Reset to first pageIndex when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get active Shopify config
  const { shopifyConfig } = useConfigurationStatus();
  const shopifyConfigId = shopifyConfig?.id;

  // Check for active customers/update webhook
  const {
    data: webhookCheck,
    isLoading: isWebhookCheckLoading
  } = useQuery({
    queryKey: ['activeCustomerUpdateWebhook', shopifyConfigId],
    queryFn: async () => {
      if (!shopifyConfigId) return null;
      const response = await checkActiveWebhookByTopic(shopifyConfigId, encodeURIComponent('customers/update'));
      return response.data;
    },
    enabled: !!shopifyConfigId,
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 0
  });

  // Fetch synced customers
  const {
    data: customersData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [
      'syncedCustomers',
      'shopify-to-erp',
      debouncedSearch,
      pageIndex,
      pageSize,
      tabChange,
      appliedFilters
    ],
    staleTime: 1000 * 60 * 2, // 2 minutes
    queryFn: async () => {
      const response = await api.getShopifyToErpSyncedCustomers({
        search: debouncedSearch?.trim() || '',
        limit: pageSize,
        offset: pageIndex * pageSize,
        updated_at_start: appliedFilters.updated_at_start || undefined,
        updated_at_end: appliedFilters.updated_at_end || undefined
      });

      // Transform the response data
      const transformedData = {
        ...response.data,
        data: response.data.data.map((customer) => ({
          ...customer,
          formattedData: formatCustomerData(customer)[0] // Get the first item since we're processing one customer at a time
        }))
      };

      return transformedData;
    }
  });

  // Handle search input change with debounce
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    updateURLWithFilters(appliedFilters, 0, pageSize, value);
  };

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithFilters(appliedFilters, newPage, pageSize, searchTerm);
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, newPageSize, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, '');
  };

  // Handle filter form changes (not applied immediately)
  const handleFilterFormChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    setAppliedFilters(filterForm);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(filterForm, 0, pageSize, searchTerm);
  };

  // Clear filters and close modal
  const handleClearFilters = () => {
    const emptyFilters = {
      updated_at_start: '',
      updated_at_end: ''
    };
    setFilterForm(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(emptyFilters, 0, pageSize, searchTerm);
  };

  // Open filter dialog and initialize form with current applied filters
  const handleOpenFilterDialog = () => {
    setFilterForm(appliedFilters);
    setIsFilterDialogOpen(true);
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some(value => value !== '');
  };

  // Check if filter form has changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(filterForm).some(key => filterForm[key] !== appliedFilters[key]);
  };

  // Modal handlers
  const handleOpenViewDialog = (customer) => {
    setViewData(customer);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewData(null);
    setIsViewDialogOpen(false);
    setIsDialogMaximized(true);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  // Format customer data
  const formatCustomerData = (customer) => {
    if (!customer) return {};

    try {
      // Handle both single customer and array of customers
      const customers = Array.isArray(customer) ? customer : [customer];

      return customers.map((customer) => {
        const customerData = customer.customer_data?.[0] || {};
        const contactData = customer.contact_data?.[0] || {};

        return {
          email:
            customer.email ||
            contactData.email ||
            customerData.email_address ||
            'N/A',
          name: contactData.name || customerData.name || 'N/A',
          customerCode: customerData.customer || 'N/A',
          shopifyCustomerId: customer.shopify_customer_id || 'N/A',
          phone: contactData.phone || customerData.phone || 'N/A',
          city: customerData['adr 4'] || 'N/A',
          state: customerData.state || 'N/A',
          generatedCustomerId: customer.generated_customer_id || 'N/A',
          updatedAt: customer.updated_at || 'N/A',
          raw: customerData
        };
      });
    } catch (error) {
      return [
        {
          email: 'Error',
          name: 'Error parsing customer data',
          customerCode: 'Error',
          shopifyCustomerId: 'Error',
          phone: 'Error',
          city: 'Error',
          state: 'Error',
          generatedCustomerId: 'Error',
          updatedAt: 'Error',
          raw: {}
        }
      ];
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      sortable: true
    },
    {
      field: 'shopify_customer_id',
      headerName: 'Shopify Customer ID',
      width: 200,
      sortable: true
    },
    {
      field: 'generated_customer_id',
      headerName: 'GCI',
      width: 150,
      sortable: true,
      align: 'left',
      renderCell: ({ value }) => value || 'N/A'
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 70,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title='View Details'>
          <IconButton size='small' onClick={() => handleOpenViewDialog(row)}>
            <ViewIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box>
      {/* Alert for missing/inactive customers/update webhook */}
      {shopifyConfigId && webhookCheck && !webhookCheck.found && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          <AlertTitle>Shopify Webhook Required</AlertTitle>
          The <b>customers/update</b> webhook is not registered or is inactive in your Shopify configuration. Customer sync from Shopify to ERP will not work until this webhook is active.
        </Alert>
      )}
      <ServerDataTable
        data={customersData?.data || []}
        columns={columns}
        sortConfig={sortConfig}
        onSort={handleSort}
        totalRows={customersData?.totalItems || 0}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoading || isFetching || isWebhookCheckLoading}
        isError={isError}
        error={error}
        noDataMessage='No synced customers found'
        totalPages={Math.ceil(customersData?.totalItems / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by Email, Shopify Customer ID, or GCI'
        searchSize='small'
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button
              variant='contained'
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
              disabled={isFetching}
            >
              Filters
              {!isFetching && Object.values(appliedFilters).some((value) => value !== '') && (
                <span>
                  <Chip
                    size='small'
                    label={isFetching ? '' : Object.values(appliedFilters).filter((value) => value !== '').length}
                    color='secondary'
                    sx={{ ml: 1 }}
                    disabled={isFetching}
                  />
                </span>
              )}
            </Button>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isFetching}>
              Refresh
            </Button>
          </Box>
        }
      />

      {/* Filter Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filter Customers
          <IconButton onClick={() => setIsFilterDialogOpen(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Divider>Updated At Range</Divider>
            <TextField
              name='updated_at_start'
              label='From Date'
              type='date'
              value={filterForm.updated_at_start}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              name='updated_at_end'
              label='To Date'
              type='date'
              value={filterForm.updated_at_end}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClearFilters}
            color='secondary'
            variant='contained'
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleApplyFilters}
            color='primary'
            variant='contained'
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Data Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='lg'
        fullWidth
        fullScreen={isDialogMaximized}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}
        >
          <Typography variant='h6' component='div'>Customer Details</Typography>
          <Box>
            <IconButton onClick={toggleDialogSize} sx={{ mr: 0.5 }}>
              {isDialogMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary'>
                  Email
                </Typography>
                <Typography variant='body1' gutterBottom sx={{wordBreak: 'break-all'}} color='text.secondary' component='div'>
                  {viewData.email ||
                    viewData.contact_data?.[0]?.email ||
                    viewData.customer_data?.[0]?.email_address ||
                    'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary'>
                  Shopify Customer ID
                </Typography>
                <Typography variant='body1' sx={{wordBreak: 'break-all'}} color='text.secondary' component='div'>
                  {viewData.shopify_customer_id || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4} sx={{wordBreak: 'break-all'}}>
                <Typography variant='body2' color='text.primary'>
                  Generated Customer ID
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.generated_customer_id || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary'>
                  Created At
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData.customer_data} title='Customer Data' />
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData.prospect_data} title='Prospect Data' />
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData.contact_data} title='Contact Data' />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDialog} color='secondary' variant='contained'>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
